import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { accent, textPrimary, bgMuted, cardStyle, primaryBtn } from './styles'
import EventProBrand from './EventProBrand'
import EventCard from './EventCard'
import PlacementZone from './PlacementZone'
import CurrentCard from './CurrentCard'
import RevealScreen from './RevealScreen'
import { formatYear, getHomeUrl, requestGame, formatElapsed } from './gameLogic'

export default function EventProGame({ playerName }) {
  const [run, setRun] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [elapsedMs, setElapsedMs] = useState(0)
  const lock = useRef(false)
  const lastPlacement = useRef(-Infinity)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    requestGame('session/', playerName).then(data => {
      if (!cancelled) setRun(data)
    }).catch(err => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [playerName])

  useEffect(() => {
    const baseline = run?.elapsed_ms || 0
    setElapsedMs(baseline)
    if (run?.status !== 'playing') return undefined
    const started = performance.now()
    const timer = setInterval(() => setElapsedMs(baseline + performance.now() - started), 30)
    return () => clearInterval(timer)
  }, [run])

  const start = async () => {
    if (lock.current) return
    lock.current = true
    setBusy(true)
    setError('')
    try { setRun(await requestGame('start/', playerName, {})) }
    catch (err) { setError(err.message) }
    finally { setBusy(false); lock.current = false }
  }

  const handlePlaceEvent = async (zone, eventId, detail) => {
    // Lock immediately, before React renders, and suppress the second click of a double tap.
    if (lock.current || run?.status !== 'playing' || eventId !== run.current_event?.id || detail > 1 || performance.now() - lastPlacement.current < 350) return
    lock.current = true
    lastPlacement.current = performance.now()
    setBusy(true)
    setError('')
    try {
      const data = await requestGame('place/', playerName, {
        run_id: run.run_id, turn: run.placements.length + 1, zone,
      })
      setRun(data)
    } catch (err) {
      // A response can be lost after the placement was saved. Read the same
      // attempt back so the next action never overwrites a committed choice.
      setError(err.message)
      try {
        setRun(await requestGame('session/', playerName))
      } catch (_) { /* Keep the last state; retrying a placement is idempotent. */ }
    } finally { setBusy(false); lock.current = false }
  }

  const header = <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
    <EventProBrand />
    <nav style={{ display: 'flex', gap: 16, fontSize: '.85rem' }}>
      <Link to="/how-to-play" style={{ color: accent }}>How to Play</Link>
      <Link to="/leaderboard" style={{ color: accent }}>Leaderboard</Link>
      <a href={getHomeUrl()} style={{ color: '#666' }}>Games Hub</a>
    </nav>
  </header>

  if (loading) return <main style={{ padding: 40, textAlign: 'center' }}>Loading your daily round…</main>
  if (run?.status === 'completed') return <RevealScreen run={run} playerName={playerName} />

  const anchor = run?.anchor
  const placedEvents = run?.placed_events || []
  const currentEvent = run?.current_event
  return <main style={{ minHeight: '100vh', backgroundColor: bgMuted, padding: '24px 16px 200px' }}>
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div inert={run?.status === 'ready' ? true : undefined}>{header}</div>
      {error && <div role="alert" style={{ ...cardStyle, color: '#991b1b', background: '#fef2f2', marginBottom: 16 }}>
        {error} {!run && <button onClick={() => window.location.reload()}>Try again</button>}
      </div>}
      {run?.status === 'ready' && <>
        <section aria-label="Timeline preview" style={{ ...cardStyle, padding: 28 }}>
          <div style={{ color: accent, fontWeight: 700, fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 20 }}>Today’s anchor</div>
          <EventCard event={anchor} isAnchor={true} showYear={true} wasStrike={false} />
          <p style={{ color: '#777', fontSize: '.85rem', marginBottom: 0 }}>Most recent events go above the anchor. Older events go below.</p>
        </section>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.2)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '24px 16px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
          <section role="dialog" aria-modal="true" aria-labelledby="round-intro-title" aria-describedby="round-intro-copy" onKeyDown={e => { if (e.key === 'Tab') { e.preventDefault(); e.currentTarget.querySelector('button').focus() } }} style={{ ...cardStyle, width: '100%', maxWidth: 500, padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>
            <h1 id="round-intro-title" style={{ marginTop: 0, fontSize: '1.4rem', color: textPrimary }}>Build your timeline</h1>
            <div id="round-intro-copy" style={{ color: '#555', lineHeight: 1.6 }}>
              <p>Place four events around this anchor. Tap a position on the timeline to place each event immediately.</p>
              <p>Most correct answers wins, then fastest time. <strong>The timer starts when you select Continue</strong> and stops after your fourth placement.</p>
            </div>
            {error && <p role="alert" style={{ color: '#991b1b' }}>{error}</p>}
            <button autoFocus onClick={start} disabled={busy} style={{ ...primaryBtn, opacity: busy ? .6 : 1 }}>{busy ? 'Starting…' : 'Continue'}</button>
          </section>
        </div>
      </>}
      {run?.status === 'playing' && <>
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div><div style={{ color: '#777', fontSize: '.8rem' }}>{playerName}</div><strong>Event {run.placements.length + 1} of 4</strong></div>
          <div style={{ textAlign: 'right' }}><div style={{ color: '#777', fontSize: '.75rem' }}>Elapsed time</div><output aria-label="Elapsed time" style={{ fontSize: '1.7rem', color: accent, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatElapsed(elapsedMs)}</output></div>
        </div>
          <p style={{ color: '#666', fontSize: '.9rem' }}>Tap a position to place the current event immediately.</p>
          {/* Timeline with zones and placed events - chronological order (earliest at bottom) */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '160px' }}>
            {/* Timeline direction indicator */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', color: '#999', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '50px' }}>
              <div style={{ textAlign: 'center' }}>
                <div>📍</div>
                <div style={{ marginTop: '4px', lineHeight: '1.2' }}>Most<br />Recent</div>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '3px' }}>↕</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div>📍</div>
                <div style={{ marginTop: '4px' }}>Oldest</div>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', flex: 1 }}>
            {(() => {
              // Separate events into before and after anchor
              const beforeAnchor = placedEvents.filter(e => e.year < anchor.year).sort((a, b) => b.year - a.year)
              const afterAnchor = placedEvents.filter(e => e.year >= anchor.year).sort((a, b) => b.year - a.year)

              return (
                <>
                  {/* Zone at top (after newest event) */}
                  <PlacementZone
                    index={afterAnchor.length + 1}
                    disabled={busy}
                    onClick={e => handlePlaceEvent(afterAnchor.length + 1, currentEvent.id, e.detail)}
                    label={afterAnchor.length === 0 ? `▲ After ${formatYear(anchor.year)}` : '▲ After all'}
                  />

                  {/* Events after anchor (newest at top) */}
                  {afterAnchor.map((event, idx) => (
                    <div key={event.id}>
                      <EventCard event={event} isAnchor={false} showYear={false} wasStrike={event.wasStrike} />
                      <PlacementZone
                        index={afterAnchor.length - idx}
                        disabled={busy}
                        onClick={e => handlePlaceEvent(afterAnchor.length - idx, currentEvent.id, e.detail)}
                        label='▼ Between'
                      />
                    </div>
                  ))}

                  {/* Anchor */}
                  <EventCard event={anchor} isAnchor={true} showYear={true} wasStrike={false} />

                  {/* Events before anchor (newest in this group at top) */}
                  {beforeAnchor.map((event, idx) => (
                    <div key={event.id}>
                      <PlacementZone
                        index={-(idx + 1)}
                        disabled={busy}
                        onClick={e => handlePlaceEvent(-(idx + 1), currentEvent.id, e.detail)}
                        label='▼ Between'
                      />
                      <EventCard event={event} isAnchor={false} showYear={false} wasStrike={event.wasStrike} />
                    </div>
                  ))}

                  {/* Zone at bottom (before oldest event) */}
                  {beforeAnchor.length > 0 && (
                    <PlacementZone
                      index={-(beforeAnchor.length + 1)}
                      disabled={busy}
                      onClick={e => handlePlaceEvent(-(beforeAnchor.length + 1), currentEvent.id, e.detail)}
                      label='▼ Before all'
                    />
                  )}

                  {beforeAnchor.length === 0 && (
                    <PlacementZone
                      index={0}
                      disabled={busy}
                      onClick={e => handlePlaceEvent(0, currentEvent.id, e.detail)}
                      label={`▼ Before ${formatYear(anchor.year)}`}
                    />
                  )}
                </>
              )
            })()}
            </div>
          </div>


        {currentEvent && <CurrentCard event={currentEvent} isLoading={busy} />}
      </>}
    </div>
  </main>
}
